import PropTypes from 'prop-types'
import React from 'react'
import classNames from 'classnames'
import { tracking } from '../../lib/analytics/mParticle'
import { TextArea } from '@paypalcorp/pp-react'
import { connect } from 'transfer/utils/p2p-connect'
import content from 'pp-react-l10n'
import { StyleSheet, css } from '../../lib/paypalphrodite'
import {
  trackAmountPageClick,
  trackClickUnifiedSend,
} from '../utils/unified-send-analytics'
import {
  ActionTypes as types,
  DONATION_NOTE_MAX_CHAR_LIMIT,
} from '../constants'
import Events from '../../lib/analytics/eventTags'
import enRTRStrings from '../../../../config/enRTRStrings'
import serverProps from '../utils/serverProps'

const styles = StyleSheet.create({
  noteWrapper: {
    boxSizing: 'border-box',
    marginBottom: '1.625rem',
  },
  textArea: {
    border: 'none',
  },
  textAreaOpen: {
    marginTop: '20px',
  },
  textAreaGiftIcon: {
    paddingRight: '2.5rem',
  },
  textAreaLabel: {
    whiteSpace: 'break-spaces',
    marginLeft: '-10px',
    marginTop: '0',
  },
})

const i18n = content('transfer/page/preview')
const i18nDonate = content('transfer/inc/note')
const { env = '' } = serverProps

class PayPalNote extends React.Component {
  static propTypes = {
    handleChangeNote: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    note: PropTypes.string,
    label: PropTypes.string,
    recipient: PropTypes.string,
    flowType: PropTypes.string,
    isReview: PropTypes.bool,
    hasFetchedFundingOptions: PropTypes.bool,
    showENSubTextRTR: PropTypes.bool,
    isDonate: PropTypes.bool,
    dispatch: PropTypes.func,
    hasGiftIcon: PropTypes.bool,
    isMissingMandatoryNote: PropTypes.bool,
  }

  static defaultProps = {
    hasFetchedFundingOptions: false,
    hasGiftIcon: false,
    disabled: false,
    note: '',
  }

  state = {
    textAreaIsFocused: false,
  }

  showNoteFieldError = () =>
    this.props.dispatch({ type: types.EXTERNAL.SHOW_NOTE_MAX_CHAR_LIMIT_ERROR })

  hideNoteFieldError = () =>
    this.props.dispatch({ type: types.EXTERNAL.HIDE_NOTE_MAX_CHAR_LIMIT_ERROR })

  hideMandatoryNoteFieldError = () => {
    this.props.dispatch({
      type: types.NOTE.REQUIRED_FIELD_ERROR,
      data: { missing: false },
    })
  }

  onTextAreaChange = ({ target: { value: note } }) => {
    const { isDonate, handleChangeNote, isMissingMandatoryNote } = this.props
    if (isDonate && note.length >= DONATION_NOTE_MAX_CHAR_LIMIT) {
      this.showNoteFieldError()
      return false
    }

    // hide mandatory message alert if isMissingMandatoryNotes flag is true and a valid note is entered
    if (isMissingMandatoryNote && note.trim().length > 0) {
      this.hideMandatoryNoteFieldError()
    }

    this.hideNoteFieldError()
    handleChangeNote(note)
  }

  onTextAreaFocus = () => {
    const { isReview, flowType, recipient, note } = this.props
    const noteHasText = Boolean(note && note.length > 0)

    if (!noteHasText && isReview) {
      tracking(Events.p2p_review_note_typing_started({}))
    }

    if (!noteHasText && !isReview) {
      tracking(Events.p2p_amount_note_typing_started({}))
    }

    if (isReview) {
      trackClickUnifiedSend(
        flowType === 'buy' ? 'onGSclickNoteField' : 'onFFclickNoteField'
      )
    } else {
      trackAmountPageClick(flowType, recipient, 'note')
    }

    this.setState({ textAreaIsFocused: true })
  }

  onBlurTextArea = () => {
    this.setState({ textAreaIsFocused: false })
  }

  render() {
    const { showENSubTextRTR, disabled, note, hasGiftIcon, label } = this.props
    const { textAreaIsFocused } = this.state

    const hasLabelProp = label && label.length > 0
    const noteHasText = note && note.length > 0

    let labelText
    if (env === 'donate') {
      labelText = hasLabelProp ? label : i18nDonate('note.default')
    } else {
      labelText = hasLabelProp ? label : i18n('note')
    }

    if (showENSubTextRTR && !hasLabelProp) {
      labelText = `${labelText} (${enRTRStrings.note})`
    }

    /* PP-React TextArea doesn't support icons inside the label at this moment, the temporary solution is to
    use `+` ASCII character in the label */
    const textAreaLabel =
      !textAreaIsFocused && !noteHasText ? `+ ${labelText}` : labelText

    const wrapperClassNames = {
      [css(styles.noteWrapper)]: true,
    }

    const textAreaClassNames = {
      [css(styles.textArea)]: true,
      [css(styles.textAreaGiftIcon)]: hasGiftIcon,
    }

    const labelClassNames = {
      [css(styles.textAreaLabel)]: !textAreaIsFocused && !noteHasText,
    }

    const textAreaProps = {
      label: textAreaLabel,
      value: note,
      disabled,
      theme: 'v2',
      maxCharacters: 1000,
      'data-nemo': 'note-field',
      inputClassName: textAreaClassNames,
      id: 'noteField',
      onChange: this.onTextAreaChange,
      onFocus: this.onTextAreaFocus,
      onBlur: this.onBlurTextArea,
    }

    const donateTextAreaProps = {
      ...textAreaProps,
      inputClassName: {
        ...textAreaClassNames,
        [css(styles.textAreaOpen)]: noteHasText || textAreaIsFocused,
      },
      labelClassName: labelClassNames,
    }

    return (
      <div className={classNames(wrapperClassNames)}>
        {env === 'donate' ? (
          <TextArea {...donateTextAreaProps} />
        ) : (
          <TextArea {...textAreaProps} />
        )}
      </div>
    )
  }
}

export default connect(({ note }) => ({
  isMissingMandatoryNote: note.isMissingMandatoryNote,
}))(PayPalNote)
